import React, { useCallback, useContext, useRef } from 'react';
import { Image, Platform, View, useWindowDimensions } from 'react-native';
import type { ViewStyle } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { SafeAreaInsetsContext } from 'react-native-safe-area-context';
import { useFocusEffect } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { Logo as DsBrandLogo, cn as dsCn, useTheme } from '@vidahealth/vida-ds';

import { AuthLayoutContext } from '../contexts/AuthLayoutContext';
import { useDsEnabled } from '../contexts/DesignSystem';
import { useLandingFooter } from '../contexts/LandingFooter';
import { useKeyboardInset } from '../hooks/useKeyboardInset';
import { cn } from '../utils/cn';
import { BrandCarousel } from './BrandCarousel/BrandCarousel';
import { useSheetHost } from './SheetHost';

interface AuthLayoutProps {
  children: React.ReactNode;
  testID?: string;
  // vida-ds entry screen: native phones open on the carousel, and the host footer rides it.
  mobileEntry?: boolean;
  // vida-ds native phones: what dismissing the sheet does. Defaults to going back.
  onDismiss?: () => void;
}

// 50dp base clearance kept below a focused field's caret when
// KeyboardAwareScrollView scrolls it above the keyboard. Sized to also clear
// the error/footnote text rendered below the outlined Input. Scaled by
// fontScale so it stays proportional at larger accessibility font sizes.
const SCROLL_TO_ERROR_BASE_OFFSET = 50;
const DESKTOP_BREAKPOINT = 768;
const DS_SPLIT_BREAKPOINT = 1024;

export const AuthLayout: React.FC<AuthLayoutProps> = (props) => {
  const dsEnabled = useDsEnabled();
  const sheetHost = useSheetHost();

  if (!dsEnabled) return <LegacyAuthLayout {...props} />;
  if (sheetHost) return <SheetPage {...props} host={sheetHost} />;
  return <DsAuthLayout {...props} />;
};

// A screen's content inside the shared phone sheet (see SheetHost), which it tells what it is.
const SheetPage: React.FC<AuthLayoutProps & { host: NonNullable<ReturnType<typeof useSheetHost>> }> = ({
  children,
  testID,
  mobileEntry = false,
  onDismiss,
  host,
}) => {
  const { fontScale } = useWindowDimensions();
  const bottomInset = useContext(SafeAreaInsetsContext)?.bottom ?? 0;
  const focused = useRef(false);
  const contentHeight = useRef(0);
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useFocusEffect(
    useCallback(() => {
      focused.current = true;
      host.activate({ entry: mobileEntry, onDismiss: onDismissRef, contentHeight: contentHeight.current });
      return () => {
        focused.current = false;
      };
    }, [host, mobileEntry])
  );

  return (
    <AuthLayoutContext.Provider value={SHEET_LAYOUT}>
      <View className='flex-1 bg-canvas' testID={testID}>
        <KeyboardAwareScrollView
          style={{ flex: 1 }}
          contentContainerClassName='px-ds-24'
          contentContainerStyle={{ paddingBottom: 24 + bottomInset }}
          keyboardShouldPersistTaps='handled'
          showsVerticalScrollIndicator={false}
          bounces={false}
          bottomOffset={Math.round(SCROLL_TO_ERROR_BASE_OFFSET * fontScale)}
          onContentSizeChange={(_width, height) => {
            contentHeight.current = height;
            if (focused.current) host.setContentHeight(height);
          }}
        >
          {children}
        </KeyboardAwareScrollView>
      </View>
    </AuthLayoutContext.Provider>
  );
};

const SHEET_LAYOUT = { brandPanel: true, inSheet: true };
// Every page layout shows the logo itself: on the carousel, or atop the page on a phone browser.
const DS_PAGE_LAYOUT = { brandPanel: true, inSheet: false };

const DsAuthLayout: React.FC<AuthLayoutProps> = ({ children, testID, mobileEntry = false }) => {
  const { width, height, fontScale } = useWindowDimensions();
  const { mode } = useTheme();
  const landingFooter = useLandingFooter();
  // A tablet's status bar and home indicator sit over the page. Read softly: no provider means no insets.
  const insets = useContext(SafeAreaInsetsContext);
  const safeTop = Platform.OS === 'web' ? 0 : (insets?.top ?? 0);
  const safeBottom = Platform.OS === 'web' ? 0 : (insets?.bottom ?? 0);
  const bottomOffset = Math.round(SCROLL_TO_ERROR_BASE_OFFSET * fontScale);
  const keyboardInset = useKeyboardInset();
  const layout = width >= DS_SPLIT_BREAKPOINT ? 'split' : width >= DESKTOP_BREAKPOINT ? 'stacked' : 'mobile';
  // The Figma tablet frame's 560px panel, shrunk on short windows so the form stays in reach.
  const stackedPanelHeight = Math.min(560, Math.max(360, Math.round(height * 0.5)));

  // The form keeps one tree position across layouts, so a breakpoint resize doesn't remount it.
  return (
    <AuthLayoutContext.Provider value={DS_PAGE_LAYOUT}>
      {/* Sits inside the legacy palette's light color-scheme; web needs it for scrollbars and autofill. */}
      <View className='flex-1 bg-canvas' style={{ colorScheme: mode } as ViewStyle} testID={testID}>
        <StatusBar style={mode === 'dark' ? 'light' : 'dark'} />
        <KeyboardAwareScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1, paddingTop: safeTop, paddingBottom: keyboardInset + safeBottom }}
          keyboardShouldPersistTaps='handled'
          showsVerticalScrollIndicator={Platform.OS === 'web'}
          bottomOffset={bottomOffset}
        >
          <View className={dsCn('flex-grow', layout === 'split' && 'flex-row p-ds-20')}>
            {layout === 'split' && (
              <View className='flex-[3] overflow-hidden rounded-ds-xl'>
                <BrandCarousel footer={mobileEntry ? landingFooter : undefined} />
              </View>
            )}
            {layout === 'stacked' && (
              <View
                className='mx-ds-20 mt-ds-20 overflow-hidden rounded-ds-xl'
                style={{ height: stackedPanelHeight }}
              >
                <BrandCarousel footer={mobileEntry ? landingFooter : undefined} description={false} />
              </View>
            )}
            {layout === 'mobile' && (
              <View className='items-center px-ds-24 pt-ds-32'>
                <View className='w-full max-w-[400px]'>
                  <DsBrandLogo variant='wordmark' width={120} />
                </View>
              </View>
            )}
            <View
              className={dsCn(
                'items-center justify-center px-ds-24 py-ds-48',
                layout === 'split' ? 'flex-[2]' : 'flex-grow'
              )}
            >
              <View className={dsCn('w-full', layout === 'stacked' ? 'max-w-[480px]' : 'max-w-[400px]')}>
                {children}
              </View>
            </View>
          </View>
        </KeyboardAwareScrollView>
      </View>
    </AuthLayoutContext.Provider>
  );
};

const LegacyAuthLayout: React.FC<AuthLayoutProps> = ({ children, testID }) => {
  const { width, fontScale } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const bottomOffset = Math.round(SCROLL_TO_ERROR_BASE_OFFSET * fontScale);
  const keyboardInset = useKeyboardInset();
  const basePadding = isDesktop ? 0 : 16;

  const renderIllustration = () => (
    <View style={{ flex: 1, backgroundColor: '#240059', justifyContent: 'center', alignItems: 'center', padding: 40, overflow: 'hidden' }}>
      {/* height: 'auto' replaces the asset's pixel height that native Image applies by default; without it, aspectRatio derived the width from 2880 and rendered 1905x2880pt on tablets. */}
      <Image
        source={require('../assets/images/vida-login-img.png')}
        style={{ width: '90%', height: 'auto', aspectRatio: 1905 / 2880 }}
        resizeMode='contain'
      />
    </View>
  );

  const form = (
    // Web-only quirk: KeyboardAwareScrollView drops its flex:1 in the desktop row without this outer wrapper. No-op on mobile.
    <View style={{ flex: 1 }}>
      <KeyboardAwareScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{
          flexGrow: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: basePadding,
          paddingTop: basePadding,
          paddingBottom: basePadding + keyboardInset,
        }}
        keyboardShouldPersistTaps='handled'
        showsVerticalScrollIndicator={false}
        bottomOffset={bottomOffset}
      >
        <View
          className={cn(!isDesktop && 'w-full max-w-[440px] bg-login-surface-pure p-10')}
          style={isDesktop ? { width: '80%' } : undefined}
        >
          {children}
        </View>
      </KeyboardAwareScrollView>
    </View>
  );

  return (
    <View className='flex-1 bg-login-surface-pure' testID={testID}>
      {isDesktop ? (
        <View style={{ flexDirection: 'row', flex: 1 }}>
          {renderIllustration()}
          {form}
        </View>
      ) : (
        form
      )}
    </View>
  );
};
